import { useImperativeHandle, useMemo, useRef, useState } from "react";

import {
  DateValidationError,
  PickerChangeHandlerContext,
} from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { useDidMount, useForceRerender } from "hooks";

import { UseDatePickerParams } from "./DatePicker.types";

export const useDatePicker = <R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseDatePickerParams<R>) => {
  const rootRef = useRef<HTMLElement | null>(null);
  const datePickerRef = useRef<HTMLElement | null>(null);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [state, setState] = useState<Dayjs | null>(
    value ?? defaultValue ?? null
  );
  const forceRerender = useForceRerender();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: datePickerRef.current,
        value: state,
        setValue: (v: Dayjs | null) => {
          setState(v);
        },
        reset: () => {
          setState(null);
        },
      };
    }) as unknown as () => R,
    [datePickerRef.current, state]
  );

  useDidMount(function init() {
    datePickerRef.current =
      rootRef.current?.querySelector(".MuiFormControl-root") ?? null;
    forceRerender();
  });

  const onChangeHandler = (
    newValue: Dayjs | null,
    context: PickerChangeHandlerContext<DateValidationError>
  ) => {
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue, context);
  };

  const onCalendarCloseHandler = () => {
    setOpenCalendar(false);
  };

  const toggleCalendar = (event?: React.PointerEvent) => {
    event?.stopPropagation();
    setOpenCalendar((prev) => !prev);
  };

  const onReset = () => {
    setState(null);
    onChange?.(null, {} as never);
    toggleCalendar();
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state),
    [value, state]
  );

  return {
    rootRef,
    datePickerRef,
    valueState,
    openCalendar,
    onChangeHandler,
    onReset,
    onCalendarCloseHandler,
    toggleCalendar,
  };
};
